import React, { useMemo } from "react";
import { XIcon } from "staysure-component-library";

import useMediaQuery from "@/app/common-hooks/use-media-query/use-media-query";
import useCoverTypeHook from "@/app/components/cover-type/hook/cover-type.hook";
import { useCoverTypeContext } from "@/app/components/cover-type/provider/cover-type.provider";
import { useQuestionLayoutContext } from "@/app/layout/question/provider/question-layout-provider";
import ModalWrapper from "@/app/shared-components/modal/modal-wrapper";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper, {
  IconButtonWrapper,
} from "@/components/atoms/button-wrapper/button-wrapper";
import ImageAtom from "@/components/atoms/image/image";
import { LoadMore } from "@/components/molecules/cta-button/cta-buttons";
import withBrand from "@/components/with-brand";
import { createImageObject } from "@/utils/cover-type.utils";

import modalStyles from "./cover-type-modal.module.css";
import DetailSection from "./detail-section/detail-section";

function CoverTypeModal() {
  const {
    magnoliaData,
    isCoverTypeModalOpen,
    setIsCoverTypeModalOpen,
    setSelectedAnswer,
  } = useCoverTypeContext();
  const { current } = useWizard();
  const { handleSubmitButton } = useCoverTypeHook();
  const { scrollToFallback } = useQuestionLayoutContext();

  const {
    modalTitle,
    modalDescription,
    primaryButtonText,
    secondaryButtonText,
    backgroundModalImageUrl,
    backgroundModalMobileImageUrl,
  } = magnoliaData.overlayArea[0];

  const [isTablet, isReady] = useMediaQuery("(min-width: 600px)");

  const brandImage = useMemo(() => {
    const selectedImageUrl =
      current.uiElementControls?.showMobileBgImage && isTablet === false
        ? backgroundModalMobileImageUrl
        : backgroundModalImageUrl;

    return typeof selectedImageUrl === "string"
      ? createImageObject(selectedImageUrl)
      : selectedImageUrl;
  }, [
    backgroundModalMobileImageUrl,
    backgroundModalImageUrl,
    current.uiElementControls?.showMobileBgImage,
    isTablet,
  ]);

  if (!isReady) return null;
  const isLandingToSamePage =
    current.uiElementControls?.landInToSamePage ?? false;

  return (
    <ModalWrapper
      className=" single-type-modal md:w-[660px] w-[327px] max-h-[90vh] overflow-y-auto"
      isOpen={isCoverTypeModalOpen}
      setIsOpen={(isOpen: boolean) => setIsCoverTypeModalOpen(isOpen)}
    >
      <div
        data-testid="cover_type_modal"
        className=" md:p-spacing-l bg-surface-neutral-option-1 rounded-lg justify-start items-center md:gap-spacing-xl flex md:flex-row  flex-col-reverse"
      >
        <div
          className="md:w-[375px] w-full flex-col justify-start items-start md:gap-spacing-m gap-[20px] 
        inline-flex relative md:px-0 px-spacing-m md:pt-0 md:pb-0 pt-[20px] pb-spacing-l"
        >
          <div
            className={`self-stretch text-text-title font-header-font md:text-[31px] text-[23px] md:leading-[34px] leading-[28px] w-[190px] md:relative absolute md:left-0 md:top-0 
            left-spacing-m top-[-100px] md:w-full md:z-0 z-[1]`}
          >
            {modalTitle}
          </div>
          {modalDescription && (
            <div
              className={`self-stretch md:text-xs md:leading-[27px] leading-[24px] text-2xs font-normal font-header-font 
              md:relative md:left-0 md:top-0 left-spacing-m md:w-full md:z-0 z-[1] ${
                current.uiElementControls?.showModalDescription
                  ? "text-black"
                  : ""
              }`}
            >
              {modalDescription}
            </div>
          )}
          <div className="self-stretch flex-col justify-start items-start gap-spacing-xs flex ">
            <DetailSection />
          </div>
          <div
            className={`self-stretch h-12 flex-col justify-center items-start 
         flex md:w-[243px] w-full ${modalStyles.btnWrapper}`}
          >
            <LabelButtonWrapper
              id="cover-type-modal-primary-button"
              data-testid="cover-type-modal-primary-button"
              className=" !text-confirmation-body"
              label={primaryButtonText ?? ""}
              hierarchy="primary"
              type="button"
              onClick={() => {
                setSelectedAnswer(2);
                setIsCoverTypeModalOpen(false);
                scrollToFallback();
              }}
            />
          </div>
          <div className="h-[18px] flex-col justify-start items-start gap-spacing-xs flex">
            <div className="self-stretch justify-center items-center inline-flex">
              <div className="h-[18px] px-0.5 pt-[3px] flex-col justify-center items-start inline-flex">
                <LoadMore
                  id="loadmoreButton-automate-test-id"
                  data-testid="loadmoreButton-automate-test-id"
                  label={secondaryButtonText ?? ""}
                  onClick={() => {
                    setSelectedAnswer(1);
                    if (isLandingToSamePage) {
                      setIsCoverTypeModalOpen(false);
                    } else {
                      handleSubmitButton();
                    }
                  }}
                  isRightIcon={false}
                  className={`text-left text-interactive-secondary-default hover:bg-interactive-secondary-pressed  body-font  text-xs font-semibold 
                  leading-[18px] underline hover:no-underline hover:text-neutral-50`}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className={`md:h-[${
            current.brandSpecificParams?.bgImgHieght as number
          }px] md:w-[205px] w-full h-[116px] relative`}
        >
          <IconButtonWrapper
            data-testid="modal-close-button"
            id="single-type-modal-close-button"
            hierarchy="tertiary"
            btnType="icon"
            className={`!absolute md:right-[-1px] md:top-[-1px]  right-spacing-xxs top-spacing-xxs md:bg-surface-neutral-option-1
                hover:!bg-surface-neutral-option-1 active:!bg-surface-neutral-option-1
              bg-transparent md:w-[45px] md:h-[35px] flex items-start justify-end !rounded-bl-radius-lg !rounded-[0px] `}
            icon={
              <>
                <XIcon stroke="#306582 " />
                <div
                  className={`${modalStyles.btnTopLeftCurve} md:block hidden`}
                />
                <div
                  className={`${modalStyles.btnBotmRightCurve} md:block hidden`}
                />
              </>
            }
            onClick={() => {
              setSelectedAnswer(1);
              setIsCoverTypeModalOpen(false);
              scrollToFallback();
            }}
          />

          <ImageAtom
            image={brandImage}
            styleClass={`w-full h-full rounded-t-radius-lg md:rounded-b-radius-lg object-cover  md:object-[${
              current.brandSpecificParams?.imageRange as number
            }%] `}
            data-testid="banner-image-testid"
          />
        </div>
      </div>
    </ModalWrapper>
  );
}

export default withBrand(CoverTypeModal);
